import type { Coordinates, Dimension, Edition, ExistingPortal, HorizontalPoint, PortalRule } from './types';

export const COORDINATE_RATIO = 8;
export const MAX_HORIZONTAL_COORDINATE = 30_000_000;

export const MAP_SPANS: Record<Dimension, number> = {
  overworld: 2_560,
  nether: 320,
};

export const PORTAL_RULES: Record<Edition, PortalRule> = {
  java: {
    label: 'Java Edition',
    overworldSearchRadius: 128,
    netherSearchRadius: 16,
    overworldDimensionId: 'minecraft:overworld',
    netherDimensionId: 'minecraft:the_nether',
    heightNote: 'Y stays unchanged. Java can use portals above the Nether ceiling when the server and world rules allow access there.',
  },
  bedrock: {
    label: 'Bedrock Edition',
    overworldSearchRadius: 128,
    netherSearchRadius: 128,
    overworldDimensionId: 'overworld',
    netherDimensionId: 'nether',
    heightNote: 'Y stays unchanged. Bedrock normally prevents building above the Nether ceiling, so keep portal Y within the playable Nether height.',
  },
};

export function parseCoordinate(value: string): number | null {
  const trimmed = value.trim();
  if (!/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && Math.abs(parsed) <= MAX_HORIZONTAL_COORDINATE ? parsed : null;
}

export function formatCoordinate(value: number): string {
  if (!Number.isFinite(value)) return '';
  const normalized = Object.is(value, -0) ? 0 : value;
  if (Number.isInteger(normalized)) return String(normalized);
  return normalized.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

export function convertOverworldToNether(coords: Coordinates): Coordinates {
  return {
    x: Math.floor(coords.x / COORDINATE_RATIO),
    y: coords.y,
    z: Math.floor(coords.z / COORDINATE_RATIO),
  };
}

export function convertNetherToOverworld(coords: Coordinates): Coordinates {
  return {
    x: coords.x * COORDINATE_RATIO,
    y: coords.y,
    z: coords.z * COORDINATE_RATIO,
  };
}

export function formatXyz(coords: Coordinates): string {
  return `${formatCoordinate(coords.x)} ${formatCoordinate(coords.y)} ${formatCoordinate(coords.z)}`;
}

export function formatTeleportCommand(edition: Edition, dimension: Dimension, coords: Coordinates): string {
  const rules = PORTAL_RULES[edition];
  const dimensionId = dimension === 'overworld' ? rules.overworldDimensionId : rules.netherDimensionId;
  return `/execute in ${dimensionId} run tp @s ${formatXyz(coords)}`;
}

export function mapPointToWorld(
  pointerX: number,
  pointerY: number,
  width: number,
  height: number,
  center: HorizontalPoint,
  blocksAcross: number,
): HorizontalPoint {
  if (width <= 0 || height <= 0 || blocksAcross <= 0) return { ...center };
  return {
    x: center.x + (pointerX / width - 0.5) * blocksAcross,
    z: center.z + (pointerY / height - 0.5) * blocksAcross,
  };
}

export function worldPointToMap(
  point: HorizontalPoint,
  center: HorizontalPoint,
  blocksAcross: number,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: width / 2 + ((point.x - center.x) / blocksAcross) * width,
    y: height / 2 + ((point.z - center.z) / blocksAcross) * height,
  };
}

export function synchronizeExistingPortal(source: Dimension, point: HorizontalPoint): ExistingPortal {
  if (source === 'overworld') {
    const overworld = { x: Math.round(point.x), z: Math.round(point.z) };
    return {
      source,
      overworld,
      nether: {
        x: Math.floor(overworld.x / COORDINATE_RATIO),
        z: Math.floor(overworld.z / COORDINATE_RATIO),
      },
    };
  }

  const nether = { x: Math.round(point.x), z: Math.round(point.z) };
  return {
    source,
    nether,
    overworld: { x: nether.x * COORDINATE_RATIO, z: nether.z * COORDINATE_RATIO },
  };
}

export function horizontalDistance(a: HorizontalPoint, b: HorizontalPoint): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

export function isPortalWithinSearchRange(center: HorizontalPoint, portal: HorizontalPoint, radius: number): boolean {
  return Math.abs(center.x - portal.x) <= radius && Math.abs(center.z - portal.z) <= radius;
}

export function searchRadiusFor(edition: Edition, dimension: Dimension): number {
  const rules = PORTAL_RULES[edition];
  return dimension === 'overworld' ? rules.overworldSearchRadius : rules.netherSearchRadius;
}
